import React, { useEffect, useMemo, useState } from 'react'
import { generatePath, matchPath, useLocation } from 'react-router-dom'

import { AppRoute } from 'routing/AppRoute.enum'
import { PulseSurvey } from 'api/actions/pulseSurveys/pulseSurveysActions.types'
import { generateCompanyURI } from 'sagas/helpers/generateCompanyURI'
import { useGetPulseSurveys } from 'hooks/pulseSurveys/useGetPulseSurveys'
import { useGetActiveCompanyInformation } from 'hooks/useGetActiveCompany'
import { useVersion } from 'hooks/useVersion'

import { PulseSurveysContextValueType } from './../pulseSurveysContext/PulseSurveysContext.types'
import { PulseSurveysContext } from './../pulseSurveysContext/PulseSurveysContext'
import { PulseSurveysContextControllerProps } from './PulseSurveysContextController.types'

export const PulseSurveysContextController = ({ children }: PulseSurveysContextControllerProps) => {
    const [activePulseSurvey, setActivePulseSurvey] = useState<PulseSurvey | null>(null)

    const [prevPathname, setPrevPathname] = useState<string | null>(null)

    const { companyInformation } = useGetActiveCompanyInformation()

    const { pathname } = useLocation()

    const { version, AVAILABLE_VERSIONS } = useVersion()

    const isV2Enabled = version === AVAILABLE_VERSIONS.v2

    const matchedPath = matchPath(pathname, {
        path: generateCompanyURI(AppRoute.PulseSurvey),
    })

    const isPulseSurveyPath = matchedPath !== null

    useGetPulseSurveys({
        onSuccess: (data) => {
            if (data.length === 0) {
                return
            }

            if (!activePulseSurvey) {
                const pulseSurvey = data[0]

                const surveyPathname = generateCompanyURI(
                    generatePath(AppRoute.PulseSurveyCreate, { id: pulseSurvey.uuid }),
                )

                setPrevPathname(surveyPathname)

                setActivePulseSurvey(pulseSurvey)
            }
        },
        enabled: companyInformation.permissions.pulseSurvey && isV2Enabled,
    })

    useEffect(() => {
        if (!isPulseSurveyPath) {
            return
        }

        if (pathname !== prevPathname) {
            setPrevPathname(pathname)
        }
    }, [isPulseSurveyPath, pathname, prevPathname])

    const handleChangeActivePulseSurvey = (pulseSurvey: PulseSurvey | null) => {
        setActivePulseSurvey(pulseSurvey)
    }

    const value: PulseSurveysContextValueType = useMemo(
        () => ({
            activePulseSurvey,
            onChangeActivePulseSurvey: handleChangeActivePulseSurvey,
            prevPathname,
        }),
        [activePulseSurvey, prevPathname],
    )

    return <PulseSurveysContext.Provider value={value}>{children}</PulseSurveysContext.Provider>
}
